(() => {
  'use strict';

  const STORAGE_KEY = 'appsalonTeslaGarage';
  const LEGACY_STORAGE_KEY = 'selectedTeslaModel';

  const DEFAULT_SEATS = 5;

  const emptyGarage = () => ({
    vehicles: [],
    activeVehicleId: null
  });

  const normalize = (value) => {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  };

  const createId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `vehicle-${Date.now()}-${Math.random()
      .toString(16)
      .slice(2)}`;
  };


  /*
   * ========================================================
   * STORAGE
   * ========================================================
   */

  const getGarage = () => {
    try {
      const stored =
        localStorage.getItem(STORAGE_KEY);

      if (!stored) {
        return emptyGarage();
      }

      const parsed = JSON.parse(stored);

      return {
        vehicles: Array.isArray(parsed.vehicles)
          ? parsed.vehicles
          : [],
        activeVehicleId:
          parsed.activeVehicleId || null
      };
    } catch (error) {
      console.warn(
        'Could not read saved Tesla vehicles.',
        error
      );

      return emptyGarage();
    }
  };


  const saveGarage = (garage) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(garage)
      );
    } catch (error) {
      console.warn(
        'Could not save Tesla vehicles.',
        error
      );
    }
  };


  const getActiveVehicle = () => {
    const garage = getGarage();

    if (!garage.activeVehicleId) {
      return null;
    }

    return (
      garage.vehicles.find(
        (vehicle) =>
          vehicle.id === garage.activeVehicleId
      ) || null
    );
  };


  const addVehicle = (vehicle) => {
    const garage = getGarage();

    const newVehicle = {
      id: createId(),

      name:
        vehicle.name || '',

      modelHandle:
        vehicle.modelHandle || '',
      model:
        vehicle.model || '',

      yearHandle:
        vehicle.yearHandle || '',
      year:
        vehicle.year || '',

      typeHandle:
        vehicle.typeHandle || '',
      type:
        vehicle.type || '',

      seatsHandle:
        vehicle.seatsHandle || '',
      seats:
        vehicle.seats ?? null,

      locationHandle:
        vehicle.locationHandle || '',
      location:
        vehicle.location || ''
    };

    garage.vehicles.push(newVehicle);
    garage.activeVehicleId = newVehicle.id;

    saveGarage(garage);

    return newVehicle;
  };


  const setActiveVehicle = (vehicleId) => {
    const garage = getGarage();

    const exists = garage.vehicles.some(
      (vehicle) => vehicle.id === vehicleId
    );

    if (!exists) {
      return null;
    }

    garage.activeVehicleId = vehicleId;

    saveGarage(garage);

    return getActiveVehicle();
  };


  const removeVehicle = (vehicleId) => {
    const garage = getGarage();

    garage.vehicles =
      garage.vehicles.filter(
        (vehicle) =>
          vehicle.id !== vehicleId
      );

    if (
      garage.activeVehicleId === vehicleId
    ) {
      garage.activeVehicleId =
        garage.vehicles[0]?.id || null;
    }

    saveGarage(garage);

    return garage;
  };


  /*
   * ========================================================
   * VEHICLE DATA
   * ========================================================
   */

  const getVehicleData = () => {
    const element =
      document.querySelector(
        '#AppsalonVehicleData'
      );

    if (!element) {
      return {
        models: [],
        years: [],
        types: [],
        seats: [],
        locations: []
      };
    }

    try {
      const data = JSON.parse(
        element.textContent
      );

      return {
        models:
          Array.isArray(data.models)
            ? data.models
            : [],

        years:
          Array.isArray(data.years)
            ? data.years
            : [],

        types:
          Array.isArray(data.types)
            ? data.types
            : [],

        seats:
          Array.isArray(data.seats)
            ? data.seats
            : [],

        locations:
          Array.isArray(data.locations)
            ? data.locations
            : []
      };
    } catch (error) {
      console.warn(
        'Could not parse Tesla vehicle data.',
        error
      );

      return {
        models: [],
        years: [],
        types: [],
        seats: [],
        locations: []
      };
    }
  };


  /*
   * ========================================================
   * LEGACY STORAGE
   * ========================================================
   */

  const migrateLegacyStorage = (
    vehicleData
  ) => {
    try {
      if (
        localStorage.getItem(STORAGE_KEY)
      ) {
        return;
      }

      const legacyModel =
        localStorage.getItem(
          LEGACY_STORAGE_KEY
        );

      if (!legacyModel) {
        return;
      }

      const modelEntry =
        vehicleData.models.find(
          (model) =>
            normalize(model.name) ===
            normalize(legacyModel)
        );

      const vehicle = {
        id: createId(),

        modelHandle:
          modelEntry?.handle || '',
        model: legacyModel,

        yearHandle: '',
        year: '',

        typeHandle: '',
        type: '',

        seatsHandle: '',
        seats: null,

        locationHandle: '',
        location: ''
      };

      saveGarage({
        vehicles: [vehicle],
        activeVehicleId: vehicle.id
      });

      localStorage.removeItem(
        LEGACY_STORAGE_KEY
      );
    } catch (error) {
      console.warn(
        'Could not migrate old Tesla model.',
        error
      );
    }
  };


  /*
   * ========================================================
   * COMPATIBILITY
   * ========================================================
   */

  const parseArray = (value) => {
    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed)
        ? parsed
        : [];
    } catch (error) {
      return [];
    }
  };


  const fieldMatches = (
    allowedValues,
    selectedValue
  ) => {
    /*
     * No values on product =
     * no restriction on this level.
     */
    if (!allowedValues.length) {
      return true;
    }

    if (!selectedValue) {
      return false;
    }

    return allowedValues.some(
      (value) =>
        String(value) ===
        String(selectedValue)
    );
  };


  const updateHeaderTrigger = () => {
    const activeVehicle =
      getActiveVehicle();

    const modal =
      document.querySelector(
        '#VehicleSelectorModal'
      );

    const defaultLabel =
      modal?.dataset.labelOpen ||
      'Velg Tesla';

    const vehicleLabel =
      activeVehicle?.model ||
      defaultLabel;

    document
      .querySelectorAll(
        '[data-vehicle-trigger-label]'
      )
      .forEach((label) => {
        label.textContent =
          vehicleLabel;
      });
  };


  const updateCompatibility = () => {
    const activeVehicle =
      getActiveVehicle();

    document
      .querySelectorAll(
        '[data-vehicle-compatibility]'
      )
      .forEach((element) => {

        if (!activeVehicle) {
          element.hidden = true;

          element.classList.remove(
            'vehicle-compatibility--compatible',
            'vehicle-compatibility--incompatible'
          );

          return;
        }


        /*
        * Start med produktnivå.
        */
        let universal =
          element.dataset
            .vehicleUniversal === 'true';

        let models = parseArray(
          element.dataset.vehicleModels
        );

        let years = parseArray(
          element.dataset.vehicleYears
        );

        let types = parseArray(
          element.dataset.vehicleTypes
        );

        let seats = parseArray(
          element.dataset.vehicleSeats
        );

        let locations = parseArray(
          element.dataset.vehicleLocations
        );


        /*
        * På produktsiden:
        * dersom valgt variant har egne Tesla-data,
        * bruker vi variantnivå i stedet.
        */
        const variantDataElement =
          element.querySelector(
            '[data-vehicle-variant-compatibility]'
          );

        if (variantDataElement) {
          try {
            const variantData =
              JSON.parse(
                variantDataElement.textContent
              );

            const variantId =
              element.dataset
                .vehicleCurrentVariant;

            const currentVariant =
              variantData[
                String(variantId)
              ];

            if (
              currentVariant &&
              currentVariant.hasData
            ) {
              universal =
                currentVariant.universal === true;

              models =
                currentVariant.models || [];

              years =
                currentVariant.years || [];

              types =
                currentVariant.types || [];

              seats =
                currentVariant.seats || [];

              locations =
                currentVariant.locations || [];
            }
          } catch (error) {
            console.warn(
              'Could not read variant Tesla compatibility.',
              error
            );
          }
        }


        /*
        * Universal overstyrer alt.
        *
        * Ellers må hvert felt som faktisk
        * er satt på produkt/variant matche.
        */
        const compatible =
          universal ||
          (
            fieldMatches(
              models,
              activeVehicle.modelHandle
            ) &&
            fieldMatches(
              years,
              activeVehicle.yearHandle
            ) &&
            fieldMatches(
              types,
              activeVehicle.typeHandle
            ) &&
            fieldMatches(
              seats,
              activeVehicle.seats
            ) &&
            fieldMatches(
              locations,
              activeVehicle.locationHandle
            )
          );


        const icon =
          element.querySelector(
            '[data-vehicle-icon]'
          );

        const title =
          element.querySelector(
            '[data-vehicle-title]'
          );

        const text =
          element.querySelector(
            '[data-vehicle-text]'
          );

        const modal =
          document.querySelector(
            '#VehicleSelectorModal'
          );

        const fitsLabel =
          modal?.dataset.labelFits ||
          'Passer din Tesla __MODEL__';

        const doesNotFitLabel =
          modal?.dataset.labelDoesNotFit ||
          'Passer ikke din Tesla __MODEL__';

        const compatibleText =
          modal?.dataset
            .textCompatible || '';

        const incompatibleText =
          modal?.dataset
            .textIncompatible || '';


        element.hidden = false;

        element.classList.toggle(
          'vehicle-compatibility--compatible',
          compatible
        );

        element.classList.toggle(
          'vehicle-compatibility--incompatible',
          !compatible
        );


        if (compatible) {
          if (icon) {
            icon.textContent = '✓';
          }

          if (title) {
            title.textContent =
              fitsLabel.replace(
                '__MODEL__',
                activeVehicle.model
              );
          }

          if (text) {
            text.textContent =
              compatibleText;
          }
        } else {
          if (icon) {
            icon.textContent = '×';
          }

          if (title) {
            title.textContent =
              doesNotFitLabel.replace(
                '__MODEL__',
                activeVehicle.model
              );
          }

          if (text) {
            text.textContent =
              incompatibleText;
          }
        }
      });
  };


  const updateVehicleUI = () => {
    updateHeaderTrigger();
    updateCompatibility();
  };

  const updateVehicleVariant = (variant) => {
    if (!variant?.id) {
      return;
    }

    document
      .querySelectorAll(
        '[data-vehicle-compatibility][data-vehicle-current-variant]'
      )
      .forEach((element) => {
        element.dataset.vehicleCurrentVariant =
          String(variant.id);
      });

    updateCompatibility();
  };


  const dispatchVehicleChange = () => {
    document.dispatchEvent(
      new CustomEvent(
        'vehicle:changed',
        {
          detail:
            getActiveVehicle()
        }
      )
    );
  };


  /*
   * ========================================================
   * INIT
   * ========================================================
   */

  const init = () => {
    const vehicleData =
      getVehicleData();

    migrateLegacyStorage(
      vehicleData
    );

    const modal =
      document.querySelector(
        '#VehicleSelectorModal'
      );

    if (!modal) {
      updateVehicleUI();
      return;
    }


    const garageView =
      modal.querySelector(
        '[data-vehicle-garage-view]'
      );

    const formView =
      modal.querySelector(
        '[data-vehicle-form-view]'
      );

    const vehicleList =
      modal.querySelector(
        '[data-vehicle-list]'
      );

    const addButton =
      modal.querySelector(
        '[data-vehicle-add]'
      );

    const backButton =
      modal.querySelector(
        '[data-vehicle-back]'
      );

    const saveButton =
      modal.querySelector(
        '[data-vehicle-save]'
      );


    const modelSelect =
      modal.querySelector(
        '[data-vehicle-model]'
      );

    const yearSelect =
      modal.querySelector(
        '[data-vehicle-year]'
      );

    const typeSelect =
      modal.querySelector(
        '[data-vehicle-type]'
      );

    const seatsSelect =
      modal.querySelector(
        '[data-vehicle-seats]'
      );

    const locationSelect =
      modal.querySelector(
        '[data-vehicle-location]'
      );

    const nameInput =
      modal.querySelector(
        '[data-vehicle-name]'
      );


    const yearField =
      modal.querySelector(
        '[data-vehicle-field="year"]'
      );

    const typeField =
      modal.querySelector(
        '[data-vehicle-field="type"]'
      );

    const seatsField =
      modal.querySelector(
        '[data-vehicle-field="seats"]'
      );

    const locationField =
      modal.querySelector(
        '[data-vehicle-field="location"]'
      );


    let lastTrigger = null;


    /*
     * ======================================================
     * SELECT HELPERS
     * ======================================================
     */

    const clearSelect = (select) => {
      if (!select) {
        return;
      }

      while (
        select.options.length > 1
      ) {
        select.remove(1);
      }

      select.value = '';
    };


    const populateSelect = (
      select,
      entries,
      labelKey = 'name'
    ) => {
      clearSelect(select);

      entries.forEach((entry) => {
        const option =
          document.createElement(
            'option'
          );

        option.value =
          entry.handle;

        option.textContent =
          entry[labelKey];

        select.appendChild(option);
      });
    };


    const hideField = (
      field,
      select
    ) => {
      if (field) {
        field.hidden = true;
      }

      clearSelect(select);
    };


    const showField = (
      field,
      select,
      entries,
      labelKey = 'name'
    ) => {
      if (!entries.length) {
        hideField(
          field,
          select
        );

        return;
      }

      populateSelect(
        select,
        entries,
        labelKey
      );

      field.hidden = false;

      /*
       * One possible option:
       * select automatically.
       */
      if (entries.length === 1) {
        select.value =
          entries[0].handle;
      }
    };


    const getSelectedEntry = (
      entries,
      select
    ) => {
      return entries.find(
        (entry) =>
          entry.handle ===
          select.value
      ) || null;
    };


    /*
     * ======================================================
     * FORM CASCADE
     * ======================================================
     */

    const resetForm = () => {
      populateSelect(
        modelSelect,
        vehicleData.models
      );

      hideField(
        yearField,
        yearSelect
      );

      hideField(
        typeField,
        typeSelect
      );

      hideField(
        seatsField,
        seatsSelect
      );

      hideField(
        locationField,
        locationSelect
      );

      if (nameInput) {
        nameInput.value = '';
      }
    };


    const updateYears = () => {
      hideField(
        yearField,
        yearSelect
      );

      hideField(
        typeField,
        typeSelect
      );

      hideField(
        seatsField,
        seatsSelect
      );

      hideField(
        locationField,
        locationSelect
      );

      if (!modelSelect.value) {
        return;
      }

      const years =
        vehicleData.years.filter(
          (entry) =>
            entry.model ===
            modelSelect.value
        );

      showField(
        yearField,
        yearSelect,
        years
      );

      if (yearSelect.value) {
        updateTypes();
      }
    };


    const updateTypes = () => {
      hideField(
        typeField,
        typeSelect
      );

      hideField(
        seatsField,
        seatsSelect
      );

      hideField(
        locationField,
        locationSelect
      );

      if (!yearSelect.value) {
        return;
      }

      const types =
        vehicleData.types.filter(
          (entry) =>
            entry.year ===
            yearSelect.value
        );

      /*
       * Some branches have no Type,
       * for example older Model X.
       */
      if (!types.length) {
        updateSeats();
        return;
      }

      showField(
        typeField,
        typeSelect,
        types
      );

      if (typeSelect.value) {
        updateSeats();
      }
    };


    const updateSeats = () => {
      hideField(
        seatsField,
        seatsSelect
      );

      hideField(
        locationField,
        locationSelect
      );

      if (!yearSelect.value) {
        return;
      }

      let seats =
        vehicleData.seats.filter(
          (entry) =>
            entry.year ===
            yearSelect.value
        );

      if (typeSelect.value) {
        seats = seats.filter(
          (entry) =>
            entry.type ===
            typeSelect.value
        );
      } else {
        /*
         * Branches without Type should
         * only use seat entries that
         * also have no Type relation.
         */
        seats = seats.filter(
          (entry) =>
            !entry.type
        );
      }

      showField(
        seatsField,
        seatsSelect,
        seats,
        'seats'
      );

      /*
       * No seat level:
       * continue directly to location.
       */
      if (!seats.length) {
        /*
        * Ingen spesifikk setekonfigurasjon.
        * 5 seter brukes som standard,
        * men feltet trenger ikke vises.
        */
        clearSelect(seatsSelect);

        seatsField.hidden = true;

        updateLocations();

        return;
      }

      if (seatsSelect.value) {
        updateLocations();
      }
    };


    const updateLocations = () => {
      hideField(
        locationField,
        locationSelect
      );

      if (!yearSelect.value) {
        return;
      }

      let locations =
        vehicleData.locations.filter(
          (entry) =>
            entry.year ===
            yearSelect.value
        );

      if (typeSelect.value) {
        locations =
          locations.filter(
            (entry) =>
              entry.type ===
              typeSelect.value
          );
      } else {
        locations =
          locations.filter(
            (entry) =>
              !entry.type
          );
      }

      if (seatsSelect.value) {
        locations =
          locations.filter(
            (entry) =>
              entry.seats ===
              seatsSelect.value
          );
      } else {
        locations =
          locations.filter(
            (entry) =>
              !entry.seats
          );
      }

      showField(
        locationField,
        locationSelect,
        locations
      );
    };


    /*
     * ======================================================
     * SAVED VEHICLE
     * ======================================================
     */

    const buildSelectedVehicle = () => {
      const model =
        getSelectedEntry(
          vehicleData.models,
          modelSelect
        );

      const year =
        getSelectedEntry(
          vehicleData.years,
          yearSelect
        );

      const type =
        getSelectedEntry(
          vehicleData.types,
          typeSelect
        );

      const seats =
        getSelectedEntry(
          vehicleData.seats,
          seatsSelect
        );

      const location =
        getSelectedEntry(
          vehicleData.locations,
          locationSelect
        );

      if (!model) {
        return null;
      }

      const customName =
        nameInput?.value.trim() || '';

      return {
        name:
          customName,

        modelHandle:
          model.handle,

        model:
          model.name,

        yearHandle:
          year?.handle,

        year:
          year?.name,

        typeHandle:
          type?.handle || '',

        type:
          type?.name || '',

        seatsHandle:
          seats?.handle || '',

        seats:
          seats?.seats ??
          DEFAULT_SEATS,

        locationHandle:
          location?.handle || '',

        location:
          location?.name || ''
      };
    };


    /*
     * ======================================================
     * GARAGE
     * ======================================================
     */

    const renderGarage = () => {
      if (!vehicleList) {
        return;
      }

      const garage =
        getGarage();

      vehicleList.innerHTML = '';

      garage.vehicles.forEach(
        (vehicle) => {
          const isActive =
            vehicle.id ===
            garage.activeVehicleId;

          const card =
            document.createElement(
              'div'
            );

          card.className =
            'vehicle-selector-vehicle';

          if (isActive) {
            card.classList.add(
              'is-active'
            );
          }


          const info =
            document.createElement(
              'div'
            );

          info.className =
            'vehicle-selector-vehicle__info';


          const title =
            document.createElement(
              'strong'
            );

          title.className =
            'vehicle-selector-vehicle__title';

          const fallbackName = [
            vehicle.model,
            vehicle.year
          ]
            .filter(Boolean)
            .join(' – ');

          title.textContent =
            vehicle.name || fallbackName;

          info.appendChild(title);


          const details = [
            vehicle.name
              ? [vehicle.model, vehicle.year]
                  .filter(Boolean)
                  .join(' – ')
              : '',

            vehicle.type,

            vehicle.seats
              ? `${vehicle.seats} ${
                  modal.dataset
                    .labelSeats ||
                  'seter'
                }`
              : '',

            vehicle.location
          ].filter(Boolean);
          

          if (details.length) {
            const meta =
              document.createElement(
                'span'
              );

            meta.className =
              'vehicle-selector-vehicle__meta';

            meta.textContent =
              details.join(' · ');

            info.appendChild(meta);
          }


          const actions =
            document.createElement(
              'div'
            );

          actions.className =
            'vehicle-selector-vehicle__actions';


          if (!isActive) {
            card.dataset.vehicleSelect =
              vehicle.id;

            card.setAttribute(
              'role',
              'button'
            );

            card.setAttribute(
              'tabindex',
              '0'
            );
          }


          const removeButton =
            document.createElement(
              'button'
            );

          removeButton.type =
            'button';

          removeButton.className =
            'vehicle-selector-vehicle__remove';

          removeButton.dataset.vehicleRemove =
            vehicle.id;

          removeButton.setAttribute(
            'aria-label',
            modal.dataset.labelRemove ||
              'Fjern bil'
          );

          removeButton.textContent = '×';

          actions.appendChild(
            removeButton
          );

          card.appendChild(info);
          card.appendChild(actions);

          vehicleList.appendChild(
            card
          );
        }
      );
    };


    /*
     * ======================================================
     * VIEWS
     * ======================================================
     */

    const showForm = () => {
      resetForm();

      garageView.hidden = true;
      formView.hidden = false;

      const garage = getGarage();

      if (backButton) {
        backButton.hidden =
          garage.vehicles.length === 0;
      }

      window.requestAnimationFrame(
        () => {
          modelSelect?.focus();
        }
      );
    };


    const showGarage = () => {
      const garage = getGarage();

      if (!garage.vehicles.length) {
        showForm();
        return;
      }

      renderGarage();

      formView.hidden = true;
      garageView.hidden = false;
    };


    /*
     * ======================================================
     * MODAL
     * ======================================================
     */

    const openModal = (trigger) => {
      lastTrigger =
        trigger || null;

      const garage =
        getGarage();

      if (garage.vehicles.length) {
        showGarage();
      } else {
        showForm();
      }

      modal.classList.add(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'false'
      );

      document
        .querySelectorAll(
          '[data-vehicle-open]'
        )
        .forEach((button) => {
          button.setAttribute(
            'aria-expanded',
            'true'
          );
        });

      document.documentElement.classList.add(
        'vehicle-modal-open'
      );
    };


    const closeModal = () => {
      modal.classList.remove(
        'is-open'
      );

      modal.setAttribute(
        'aria-hidden',
        'true'
      );

      document
        .querySelectorAll(
          '[data-vehicle-open]'
        )
        .forEach((button) => {
          button.setAttribute(
            'aria-expanded',
            'false'
          );
        });

      document.documentElement.classList.remove(
        'vehicle-modal-open'
      );

      if (lastTrigger) {
        lastTrigger.focus();
      }
    };


    /*
     * ======================================================
     * EVENTS
     * ======================================================
     */

    document.addEventListener(
      'krown:variant:change',
      (event) => {
        const variant =
          event.detail?.variant;

        if (!variant) {
          return;
        }

        updateVehicleVariant(
          variant
        );
      }
    );

    modelSelect?.addEventListener(
      'change',
      updateYears
    );

    yearSelect?.addEventListener(
      'change',
      updateTypes
    );

    typeSelect?.addEventListener(
      'change',
      updateSeats
    );

    seatsSelect?.addEventListener(
      'change',
      updateLocations
    );


    addButton?.addEventListener(
      'click',
      showForm
    );

    backButton?.addEventListener(
      'click',
      showGarage
    );


    saveButton?.addEventListener(
      'click',
      () => {
        if (!modelSelect.value) {
          modelSelect.focus();
          return;
        }

        const vehicle =
          buildSelectedVehicle();

        if (!vehicle) {
          console.warn(
            'Could not build selected Tesla.'
          );

          return;
        }

        addVehicle(vehicle);

        updateVehicleUI();
        dispatchVehicleChange();

        showGarage();
      }
    );


    modal.addEventListener(
      'click',
      (event) => {

        const removeButton =
          event.target.closest(
            '[data-vehicle-remove]'
          );

        if (removeButton) {
          event.preventDefault();
          event.stopPropagation();

          removeVehicle(
            removeButton.dataset
              .vehicleRemove
          );

          updateVehicleUI();
          dispatchVehicleChange();

          const garage =
            getGarage();

          if (
            garage.vehicles.length
          ) {
            renderGarage();
          } else {
            showForm();
          }

          return;
        }


        const selectButton =
          event.target.closest(
            '[data-vehicle-select]'
          );

        if (selectButton) {
          const vehicle =
            setActiveVehicle(
              selectButton.dataset
                .vehicleSelect
            );

          if (vehicle) {
            renderGarage();
            updateVehicleUI();
            dispatchVehicleChange();
            closeModal();
          }

          return;
        }
      }
    );

    modal.addEventListener(
      'keydown',
      (event) => {
        const card =
          event.target.closest(
            '[data-vehicle-select]'
          );

        if (
          !card ||
          (
            event.key !== 'Enter' &&
            event.key !== ' '
          )
        ) {
          return;
        }

        event.preventDefault();

        const vehicle =
          setActiveVehicle(
            card.dataset.vehicleSelect
          );

        if (vehicle) {
          renderGarage();
          updateVehicleUI();
          dispatchVehicleChange();
          closeModal();
        }
      }
    );


    document.addEventListener(
      'click',
      (event) => {
        const openButton =
          event.target.closest(
            '[data-vehicle-open]'
          );

        if (openButton) {
          event.preventDefault();
          openModal(openButton);
          return;
        }

        const closeButton =
          event.target.closest(
            '[data-vehicle-close]'
          );

        if (closeButton) {
          event.preventDefault();
          closeModal();
        }
      }
    );


    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key === 'Escape' &&
          modal.classList.contains(
            'is-open'
          )
        ) {
          closeModal();
        }
      }
    );


    /*
     * Maranello can replace product cards
     * when filtering/sorting.
     */
    const observer =
      new MutationObserver(
        (mutations) => {

          const compatibilityAdded =
            mutations.some(
              (mutation) => {
                return Array.from(
                  mutation.addedNodes
                ).some((node) => {

                  if (
                    !(
                      node instanceof
                      HTMLElement
                    )
                  ) {
                    return false;
                  }

                  return (
                    node.matches?.(
                      '[data-vehicle-compatibility]'
                    ) ||
                    node.querySelector?.(
                      '[data-vehicle-compatibility]'
                    )
                  );
                });
              }
            );

          if (compatibilityAdded) {
            updateCompatibility();
          }
        }
      );


    observer.observe(
      document.body,
      {
        childList: true,
        subtree: true
      }
    );


    resetForm();
    updateVehicleUI();
  };


  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      init
    );
  } else {
    init();
  }
})();